import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Receipt } from "lucide-react";

export default function TaxSettingsTab() {
    const [form, setForm] = useState({ mode: "inclusive", default_gst_rate: 18, label: "GST" });
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/tax-settings").then(({ data }) => {
            setForm({
                mode: data.mode || "inclusive",
                default_gst_rate: data.default_gst_rate ?? 18,
                label: data.label || "GST",
            });
            setLoaded(true);
        });
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: name === "default_gst_rate" ? Number(value) : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/admin/tax-settings", form);
            toast.success("Tax settings saved");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return <div className="text-center py-10 text-ink-700">Loading…</div>;

    return (
        <div className="max-w-2xl space-y-6" data-testid="tax-settings-tab">
            <div>
                <h3 className="font-heading text-2xl">Tax (GST) settings</h3>
                <p className="text-sm text-ink-700">Choose how product prices are displayed and the fallback GST rate when a product has no category.</p>
            </div>
            <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-gray-100 p-6 space-y-5">
                <div>
                    <Label className="mb-3 block">Price display mode</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className={`flex items-start gap-3 rounded-2xl border-2 p-4 cursor-pointer transition ${form.mode === "inclusive" ? "border-brand-500 bg-brand-50" : "border-gray-200 hover:border-brand-300"}`} data-testid="tax-mode-inclusive">
                            <input type="radio" name="mode" value="inclusive" checked={form.mode === "inclusive"} onChange={onChange} className="mt-1" />
                            <div>
                                <div className="font-semibold">Tax-inclusive</div>
                                <div className="text-xs text-ink-700 mt-0.5">Listed price already includes GST. Indian-store norm.</div>
                            </div>
                        </label>
                        <label className={`flex items-start gap-3 rounded-2xl border-2 p-4 cursor-pointer transition ${form.mode === "exclusive" ? "border-brand-500 bg-brand-50" : "border-gray-200 hover:border-brand-300"}`} data-testid="tax-mode-exclusive">
                            <input type="radio" name="mode" value="exclusive" checked={form.mode === "exclusive"} onChange={onChange} className="mt-1" />
                            <div>
                                <div className="font-semibold">Tax-exclusive</div>
                                <div className="text-xs text-ink-700 mt-0.5">GST is added on top at checkout.</div>
                            </div>
                        </label>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Default GST rate (%)</Label>
                        <Input type="number" name="default_gst_rate" min={0} max={100} step={0.5} value={form.default_gst_rate} onChange={onChange} className="mt-1.5 rounded-xl" data-testid="tax-default-rate" />
                        <p className="text-xs text-ink-700 mt-1">Used when a product's category has no GST rate set.</p>
                    </div>
                    <div>
                        <Label>Label</Label>
                        <Input name="label" value={form.label} onChange={onChange} className="mt-1.5 rounded-xl" />
                        <p className="text-xs text-ink-700 mt-1">Shown in cart breakdown (default: "GST").</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-paper-alt p-4">
                    <Receipt className="h-5 w-5 text-brand-600" />
                    <p className="text-xs text-ink-700">
                        Per-category GST rates are managed under the <strong>Categories</strong> tab. Each product inherits its category's rate; products without a category fall back to this default.
                    </p>
                </div>
                <div className="flex justify-end">
                    <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="tax-settings-save">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
